/**
 * CategoryCombobox — searchable, grouped category picker.
 *
 * Replaces the plain <Select> everywhere a category is chosen. The taxonomy is
 * ~60 leaves across 5 type groups, and users were scrolling the whole list to
 * find one (reported 2026-08-20). Typing filters on both the category name and
 * its group label, so "invest" surfaces every Investment category and "food"
 * jumps straight to Food & Dining.
 *
 * Popover + cmdk rather than a search box inside a Radix Select: Select owns
 * keyboard input for its own typeahead, so a text field inside it never
 * behaves. The trigger deliberately reuses SelectTrigger's classes so the
 * control is visually identical to the other filters beside it.
 */
import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { categoryLabel } from '@/lib/categorize';

const TRIGGER_BASE =
  'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border '
  + 'border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background '
  + 'focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed '
  + 'disabled:opacity-50 transition-colors hover:bg-accent/30';

export function CategoryCombobox({
  groups = [],
  value,
  onChange,
  placeholder = 'Pick one',
  searchPlaceholder = 'Search categories…',
  /** Second line of the no-results state. Type-scoped call sites explain the scope. */
  emptyHint = 'Try a shorter word.',
  /** Ungrouped entries pinned above the groups, e.g. [{ value:'all', label:'All categories' }] */
  leadingOptions = [],
  triggerClassName,
  contentClassName,
  testId,
  disabled = false,
  id,
}) {
  const [open, setOpen] = useState(false);

  const label = useMemo(() => {
    if (!value) return null;
    const lead = leadingOptions.find((o) => o.value === value);
    if (lead) return lead.label;
    for (const g of groups) {
      if ((g.items || []).includes(value)) return categoryLabel(value);
    }
    return categoryLabel(value);   // a category no longer in the taxonomy still reads back
  }, [value, groups, leadingOptions]);

  const pick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          className={cn(TRIGGER_BASE, !label && 'text-muted-foreground', triggerClassName)}
          data-testid={testId}
        >
          <span className="truncate">{label || placeholder}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-[--radix-popover-trigger-width] min-w-[240px] p-0', contentClassName)}
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Command
          // Match on the category name AND its group label so "invest" or
          // "transfer" narrows to a whole section.
          filter={(itemValue, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${itemValue} ${(keywords || []).join(' ')}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>
              <div className="text-sm">No category matches that.</div>
              <div className="text-xs text-muted-foreground mt-1">{emptyHint}</div>
            </CommandEmpty>
            {leadingOptions.length > 0 && (
              <CommandGroup>
                {leadingOptions.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => pick(o.value)}
                    className="cursor-pointer"
                    data-testid={testId ? `${testId}-item-${o.value}` : undefined}
                  >
                    <Check className={cn('h-4 w-4', value === o.value ? 'opacity-100' : 'opacity-0')} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {groups.map((g, gi) => (
              <CommandGroup
                key={g.key || g.label || `g-${gi}`}
                heading={g.label || undefined}
                data-testid={testId && g.key ? `${testId}-group-${g.key}` : undefined}
              >
                {(g.items || []).map((c) => (
                  <CommandItem
                    key={`${g.label || gi}-${c}`}
                    value={categoryLabel(c)}
                    keywords={g.label ? [g.label, c] : [c]}
                    onSelect={() => pick(c)}
                    className="cursor-pointer"
                    data-testid={testId ? `${testId}-item-${c}` : undefined}
                  >
                    <Check className={cn('h-4 w-4', value === c ? 'opacity-100' : 'opacity-0')} />
                    {categoryLabel(c)}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CategoryCombobox;
